import { useEffect, useRef, useState } from 'react';
import { MapPicker } from './MapPicker';

export type LocationSource = 'default' | 'geolocation' | 'search' | 'manual' | 'pinned';

export interface Location {
  lat: number;
  lng: number;
  label: string;
  source: LocationSource;
}

interface Props {
  value: Location;
  onChange: (loc: Location) => void;
  autoRequestGeolocation?: boolean;
}

export const DEFAULT_LOCATION: Location = {
  lat: 37.7749,
  lng: -122.4194,
  label: 'San Francisco',
  source: 'default',
};

export function LocationPicker({ value, onChange, autoRequestGeolocation = true }: Props) {
  const [query, setQuery] = useState(value.label);
  const [searching, setSearching] = useState(false);
  const [status, setStatus] = useState<string | null>(null);
  const triedAutoGeo = useRef(false);
  const inputRef = useRef<HTMLInputElement>(null);

  // Auto-request geolocation on mount if permission is already granted and
  // the caller is still on the default — don't overwrite a deliberate pick.
  useEffect(() => {
    if (!autoRequestGeolocation || triedAutoGeo.current) return;
    if (value.source !== 'default') return;
    triedAutoGeo.current = true;

    const tryGeo = async () => {
      if (!navigator.geolocation) return;
      try {
        const perm = await navigator.permissions?.query({ name: 'geolocation' as PermissionName });
        if (perm && perm.state === 'denied') return;
        if (perm && perm.state !== 'granted') return;
      } catch {
        /* permissions API not supported — skip */
      }
      navigator.geolocation.getCurrentPosition(
        (pos) => onChange({
          lat: pos.coords.latitude,
          lng: pos.coords.longitude,
          label: 'Your location',
          source: 'geolocation',
        }),
        () => { /* silent fail on auto path */ },
        { enableHighAccuracy: false, timeout: 5000 },
      );
    };

    void tryGeo();
  }, [autoRequestGeolocation, value.source, onChange]);

  // Sync the displayed label when location changes from outside (map click,
  // geolocation) — but leave the user's in-progress typing untouched.
  useEffect(() => {
    if (document.activeElement !== inputRef.current) {
      setQuery(value.label);
    }
  }, [value.label]);

  const requestGeolocation = () => {
    setStatus(null);
    if (!navigator.geolocation) {
      setStatus('Geolocation not available. Search for a place instead.');
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => onChange({
        lat: pos.coords.latitude,
        lng: pos.coords.longitude,
        label: 'Your location',
        source: 'geolocation',
      }),
      (err) => setStatus(err.message),
      { enableHighAccuracy: false, timeout: 8000 },
    );
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    const raw = query.trim();
    if (!raw) return;

    // If it looks like "lat, lng" coordinates, apply them directly.
    const coordMatch = raw.match(/^(-?\d+(?:\.\d+)?)\s*,\s*(-?\d+(?:\.\d+)?)$/);
    if (coordMatch) {
      const lat = parseFloat(coordMatch[1]);
      const lng = parseFloat(coordMatch[2]);
      if (lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180) {
        onChange({ lat, lng, label: `${lat.toFixed(4)}, ${lng.toFixed(4)}`, source: 'manual' });
        inputRef.current?.blur();
        return;
      }
    }

    setSearching(true);
    setStatus(null);
    try {
      const res = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&limit=1&q=${encodeURIComponent(raw)}`,
        { headers: { 'Accept-Language': 'en' } },
      );
      const results = (await res.json()) as Array<{ lat: string; lon: string; display_name: string }>;
      if (!results[0]) {
        setStatus('No match — try a bigger city name.');
        return;
      }
      onChange({
        lat: parseFloat(results[0].lat),
        lng: parseFloat(results[0].lon),
        label: results[0].display_name,
        source: 'search',
      });
      inputRef.current?.blur();
    } catch (err) {
      setStatus(err instanceof Error ? err.message : 'search failed');
    } finally {
      setSearching(false);
    }
  };

  const { lat, lng } = value;

  return (
    <div className="stack">
      <MapPicker
        lat={lat}
        lng={lng}
        onPick={(lat, lng) => onChange({ lat, lng, label: 'Pinned on map', source: 'pinned' })}
      />

      <div className="row" style={{ justifyContent: 'space-between' }}>
        <button
          type="button"
          className="ghost"
          onClick={requestGeolocation}
          style={{ textAlign: 'left', padding: 0 }}
        >
          Use my current location
        </button>
      </div>

      <form onSubmit={submit}>
        <div className="location-field">
          <span aria-hidden>📍</span>
          <input
            ref={inputRef}
            id="loc-q"
            type="text"
            placeholder="Search a place or enter lat, lng…"
            aria-label="Location — search a place or enter lat, lng"
            value={searching ? '…' : query}
            disabled={searching}
            onChange={(e) => setQuery(e.target.value)}
            onFocus={() => setQuery('')}
            onBlur={() => setQuery(value.label)}
          />
          <button
            type="submit"
            className="secondary"
            // Prevent the button from blurring the input before submit. A mouse
            // click would otherwise fire the input's onBlur, which resets `query`
            // to value.label before submit() reads it — so the search would run
            // against the old/pre-selected value instead of what's typed. Keydown
            // (Enter) doesn't blur, which is why it worked. Genuine click-outs
            // elsewhere still clear/reset the field as intended.
            onMouseDown={(e) => e.preventDefault()}
            disabled={searching || !query.trim()}
          >
            {searching ? '…' : 'Find'}
          </button>
        </div>
      </form>

      {status && <p className="muted">{status}</p>}
    </div>
  );
}
